import { apiClient } from '@/lib/api-client';
import type {
  ApiResponse,
  PaginatedResponse,
  Gateway,
  GatewayDevice,
  CreateGatewayRequest,
  UpdateGatewayRequest,
  GatewayCertificate,
} from '@/types/api';

export interface GetGatewaysParams {
  search?: string;
  propertyId?: string;
  page?: number;
  perPage?: number;
}

export async function getGateways(params: GetGatewaysParams = {}): Promise<PaginatedResponse<Gateway>> {
  const response = await apiClient.get<PaginatedResponse<Gateway>>('/gateways', { params });
  return response.data;
}

export async function getGateway(id: string): Promise<ApiResponse<Gateway>> {
  const response = await apiClient.get<ApiResponse<Gateway>>(`/gateways/${id}`);
  return response.data;
}

export async function createGateway(data: CreateGatewayRequest): Promise<ApiResponse<Gateway>> {
  const response = await apiClient.post<ApiResponse<Gateway>>('/gateways', data);
  return response.data;
}

export async function updateGateway(id: string, data: UpdateGatewayRequest): Promise<ApiResponse<null>> {
  const response = await apiClient.put<ApiResponse<null>>(`/gateways/${id}`, data);
  return response.data;
}

// Notes are writable by any authenticated role (other fields require Owner).
export async function updateGatewayNotes(id: string, notes: string | null | undefined): Promise<void> {
  await apiClient.patch(`/gateways/${id}/notes`, { notes });
}

export async function deleteGateway(id: string): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(`/gateways/${id}`);
  return response.data;
}

export async function createGatewayCertificate(id: string): Promise<ApiResponse<GatewayCertificate>> {
  const response = await apiClient.post<ApiResponse<GatewayCertificate>>(`/gateways/${id}/certificate`);
  return response.data;
}

export async function deleteGatewayCertificate(id: string): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(`/gateways/${id}/certificate`);
  return response.data;
}

export async function getServerTrustCertificate(): Promise<ApiResponse<string>> {
  const response = await apiClient.get<ApiResponse<string>>('/gateways/server-trust-certificate');
  return response.data;
}

export async function getPropertyGateways(propertyId: string): Promise<Gateway[]> {
  const response = await apiClient.get<Gateway[]>(`/properties/${propertyId}/gateways`);
  return response.data;
}

export async function assignGatewayToProperty(propertyId: string, gatewayEui: string): Promise<ApiResponse<Gateway>> {
  const response = await apiClient.post<ApiResponse<Gateway>>(`/properties/${propertyId}/gateways`, { gatewayEui });
  return response.data;
}

export async function getGatewayDevices(gatewayId: string): Promise<GatewayDevice[]> {
  const response = await apiClient.get<GatewayDevice[]>(`/gateways/${gatewayId}/devices`);
  return response.data;
}
